import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, X, Check, Crosshair } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useActiveEvent } from '../../contexts/EventContext';

interface MapLocationPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (lat: number, lng: number, address: string) => void;
  initialCenter?: [number, number];
}

interface SerializedDrawing {
  id: string;
  type: 'marker' | 'circle' | 'polyline' | 'polygon';
  label?: string;
  notes?: string;
  color?: string;
  latlng?: L.LatLngExpression;
  latlngs?: L.LatLngExpression[] | L.LatLngExpression[][];
  radius?: number;
}

export default function MapLocationPicker({
  open,
  onClose,
  onSelect,
  initialCenter = [51.505, -0.09],
}: MapLocationPickerProps) {
  const { activeEventId } = useActiveEvent();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const drawingsLayerRef = useRef<L.FeatureGroup>(new L.FeatureGroup());
  const [pickedLatLng, setPickedLatLng] = useState<[number, number] | null>(null);
  const [address, setAddress] = useState('');
  const [reverseGeocoding, setReverseGeocoding] = useState(false);

  // Load drawings from localStorage (event-scoped)
  const loadDrawings = () => {
    try {
      const storageKey = activeEventId ? `c2_map_drawings_${activeEventId}` : 'c2_map_drawings';
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const serialized: SerializedDrawing[] = JSON.parse(stored);
        return serialized;
      }
    } catch (error) {
      console.error('Error loading drawings:', error);
    }
    return [];
  };

  // Format a Nominatim address result into: [number] [street], [borough], [county/london], [postcode]
  const formatAddress = (data: any, lat: number, lng: number): string => {
    if (!data || !data.address) {
      return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    }
    const a = data.address;
    const houseNumber = a.house_number || '';
    const street = a.road || a.pedestrian || a.footway || a.path || '';
    const borough =
      a.suburb ||
      a.neighbourhood ||
      a.city_district ||
      a.town ||
      a.village ||
      a.hamlet ||
      '';
    // For UK: prefer recognised county-level region. For London use "London".
    let county = '';
    const region = a.state_district || a.county || a.state || '';
    if (/london/i.test(region) || /london/i.test(a.city || '')) {
      county = 'London';
    } else {
      county = region;
    }
    const postcode = a.postcode || '';

    const streetLine = [houseNumber, street].filter(Boolean).join(' ');
    const parts = [streetLine, borough, county, postcode].filter((p) => p && p.trim());
    return parts.join(', ') || data.display_name || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  };

  useEffect(() => {
    if (!open || !containerRef.current) return;
    if (mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: initialCenter,
      zoom: 13,
      zoomControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    // Add drawings layer
    drawingsLayerRef.current.addTo(map);

    // Load and display existing drawings
    const drawings = loadDrawings();
    drawings.forEach(drawing => {
      let layer: L.Layer | null = null;

      try {
        if (drawing.type === 'marker' && drawing.latlng) {
          layer = L.marker(drawing.latlng as L.LatLngExpression);
        } else if (drawing.type === 'circle' && drawing.latlng && drawing.radius) {
          layer = L.circle(drawing.latlng as L.LatLngExpression, {
            radius: drawing.radius,
            color: drawing.color || '#2563eb',
            fillColor: drawing.color || '#2563eb',
            fillOpacity: 0.2,
          });
        } else if (drawing.type === 'polyline' && drawing.latlngs) {
          layer = L.polyline(drawing.latlngs as L.LatLngExpression[], {
            color: drawing.color || '#f97316',
          });
        } else if (drawing.type === 'polygon' && drawing.latlngs) {
          layer = L.polygon(drawing.latlngs as L.LatLngExpression[][], {
            color: drawing.color || '#7c3aed',
            fillColor: drawing.color || '#7c3aed',
            fillOpacity: 0.2,
          });
        }

        if (layer) {
          // Add popup with drawing info
          const popupContent = `
            <div style="padding: 4px;">
              <div style="font-weight: bold; font-size: 11px; margin-bottom: 2px;">${drawing.label || drawing.type}</div>
              ${drawing.notes ? `<div style="font-size: 10px; color: #666;">${drawing.notes}</div>` : ''}
              <div style="font-size: 9px; color: #999; margin-top: 4px; font-style: italic;">Click to use as incident location</div>
            </div>
          `;
          layer.bindPopup(popupContent, { maxWidth: 200 });
          
          // Add click handler to use drawing location
          layer.on('click', (e: L.LeafletMouseEvent) => {
            L.DomEvent.stopPropagation(e); // Prevent map click from firing
            
            let locationLatLng: L.LatLng;
            
            // Get appropriate location based on drawing type
            if (drawing.type === 'marker') {
              locationLatLng = (layer as L.Marker).getLatLng();
            } else if (drawing.type === 'circle') {
              locationLatLng = (layer as L.Circle).getLatLng();
            } else if (drawing.type === 'polygon') {
              // Use polygon center
              const bounds = (layer as L.Polygon).getBounds();
              locationLatLng = bounds.getCenter();
            } else if (drawing.type === 'polyline') {
              // Use polyline center
              const bounds = (layer as L.Polyline).getBounds();
              locationLatLng = bounds.getCenter();
            } else {
              return;
            }
            
            const { lat, lng } = locationLatLng;
            setPickedLatLng([lat, lng]);
            
            // Update or create marker at this location
            if (markerRef.current) {
              markerRef.current.setLatLng(locationLatLng);
            } else {
              markerRef.current = L.marker(locationLatLng, {
                icon: L.divIcon({
                  className: 'custom-pick-marker',
                  html: `<div style="width:20px;height:20px;border-radius:50%;background:#2563eb;border:3px solid #fff;box-shadow:0 0 12px #2563eb80;"></div>`,
                  iconSize: [20, 20],
                  iconAnchor: [10, 10],
                }),
              }).addTo(map);
            }
            
            // Build a prefix from the drawing's label/type
            const typeLabel: Record<string, string> = {
              marker: 'MARKER',
              circle: 'CIRCLE',
              polygon: 'ZONE',
              polyline: 'ROUTE',
            };
            const prefix = drawing.label
              ? `${typeLabel[drawing.type] || ''} ${drawing.label}`.trim().toUpperCase()
              : (typeLabel[drawing.type] || drawing.type.toUpperCase());

            // Reverse geocode
            setReverseGeocoding(true);
            fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`)
              .then((res) => res.json())
              .then((data) => {
                const formatted = formatAddress(data, lat, lng);
                setAddress(`${prefix}, ${formatted}`);
              })
              .catch(() => {
                setAddress(`${prefix}, ${lat.toFixed(6)}, ${lng.toFixed(6)}`);
              })
              .finally(() => setReverseGeocoding(false));
          });
          
          drawingsLayerRef.current.addLayer(layer);
        }
      } catch (error) {
        console.error('Error adding drawing to map:', error);
      }
    });

    map.on('click', (e: L.LeafletMouseEvent) => {
      const { lat, lng } = e.latlng;
      setPickedLatLng([lat, lng]);

      if (markerRef.current) {
        markerRef.current.setLatLng(e.latlng);
      } else {
        markerRef.current = L.marker(e.latlng, {
          icon: L.divIcon({
            className: 'custom-pick-marker',
            html: `<div style="width:20px;height:20px;border-radius:50%;background:#2563eb;border:3px solid #fff;box-shadow:0 0 12px #2563eb80;"></div>`,
            iconSize: [20, 20],
            iconAnchor: [10, 10],
          }),
        }).addTo(map);
      }

      // Reverse geocode using Nominatim
      setReverseGeocoding(true);
      fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`)
        .then((res) => res.json())
        .then((data) => {
          setAddress(formatAddress(data, lat, lng));
        })
        .catch(() => {
          setAddress(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
        })
        .finally(() => setReverseGeocoding(false));
    });

    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 100);

    return () => {
      drawingsLayerRef.current.clearLayers();
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, [open]);

  const handleConfirm = () => {
    if (pickedLatLng && address) {
      onSelect(pickedLatLng[0], pickedLatLng[1], address);
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl bg-card border border-border rounded-xl shadow-2xl flex flex-col overflow-hidden" style={{ height: '70vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
          <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
            <Crosshair className="w-4 h-4 text-primary" />
            Select Location on Map
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-accent rounded-lg transition-colors">
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Instruction */}
        <div className="px-4 py-2 bg-primary/5 border-b border-border flex-shrink-0">
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <MapPin className="w-3 h-3 text-primary" />
            Click on the map to set the incident location. The address will be auto-detected.
          </p>
        </div>

        {/* Map */}
        <div ref={containerRef} className="flex-1 min-h-0" />

        {/* Footer with selected location */}
        <div className="px-4 py-3 border-t border-border flex-shrink-0 bg-card">
          {pickedLatLng ? (
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-muted-foreground font-mono mb-0.5">
                  {pickedLatLng[0].toFixed(6)}, {pickedLatLng[1].toFixed(6)}
                </p>
                <p className={cn('text-xs text-foreground truncate', reverseGeocoding && 'text-muted-foreground italic')}>
                  {reverseGeocoding ? 'Detecting address...' : address}
                </p>
              </div>
              <button
                onClick={handleConfirm}
                disabled={reverseGeocoding}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                <Check className="w-3.5 h-3.5" />
                Confirm Location
              </button>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground text-center">Click anywhere on the map to select a location</p>
          )}
        </div>
      </div>
    </div>
  );
}
